/**
 * CRM → Marketing tab.
 *
 * Three sub-tabs: Lists · Broadcasts · Templates. Live-refreshes the stats
 * strip on top so admins can see contact-count / subscription counts at a
 * glance. All data comes from /api/marketing/*.
 */
import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Users2, Mail, FileText, Send, Clock, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const SUB_TABS = [
  { to: "/app/crm/marketing", end: true, label: "Lists", icon: Users2, tid: "mkt-tab-lists" },
  { to: "/app/crm/marketing/broadcasts", label: "Broadcasts", icon: Send, tid: "mkt-tab-broadcasts" },
  { to: "/app/crm/marketing/templates", label: "Templates", icon: FileText, tid: "mkt-tab-templates" },
];

export default function Marketing() {
  const [stats, setStats] = useState(null);

  const load = useCallback(() => {
    api.get("/marketing/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 30_000); return () => clearInterval(iv); }, [load]);

  return (
    <div className="space-y-6" data-testid="crm-marketing-page">
      <div>
        <div className="overline text-secondary">Marketing</div>
        <h2 className="mt-1 font-display text-3xl font-light tracking-tight">Email marketing engine</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Lists, broadcasts and templates — all powered by our built-in Resend integration. No external accounts required.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatTile icon={<Mail className="w-4 h-4" />} label="Contacts" value={stats.total_contacts} tid="mkt-stat-contacts" />
          <StatTile icon={<Users2 className="w-4 h-4" />} label="Subscribed" value={stats.subscribed} />
          <StatTile icon={<AlertTriangle className="w-4 h-4" />} label="Unsubscribed" value={stats.unsubscribed} />
          <StatTile icon={<FileText className="w-4 h-4" />} label="Lists" value={stats.lists} />
          <StatTile icon={<Clock className="w-4 h-4" />} label="Scheduled" value={stats.scheduled_broadcasts} tid="mkt-stat-scheduled" />
          <StatTile icon={<Send className="w-4 h-4" />} label="Sent this run" value={stats.delivery?.sent || 0} />
        </div>
      )}

      <div className="border-b border-border overflow-x-auto">
        <nav className="flex gap-1 min-w-max">
          {SUB_TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.tid}
              className={({ isActive }) =>
                `px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`
              }
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <Outlet />
    </div>
  );
}

function StatTile({ icon, label, value, tid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={tid}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="mt-1 font-display text-2xl">{value ?? 0}</div>
    </div>
  );
}
